import * as React from "react";
import { RefObject } from "react";
import "./Reports.scss";
import Report from "./reports/Report";
import PagedModel from "./commons/components/pagination/PagedModel";
import Column from "./commons/components/pagination/Column";
import ReportIcon from "../resources/images/report.svg";
import Sort from "./commons/components/pagination/Sort";
import HeaderBar from "./commons/components/HeaderBar";
import ReportList from "./reports/ReportList";
import MobileApplicationView from "./patient-portal/MobileApplicationView";
import MobileApplicationLink from "./patient-portal/MobileApplicationLink";
import { activate as oberver_polyfill_activate } from "../polyfills/resize-observer-polyfill";
import ReportFragment from "./reports/ReportFragment";
import { createRoot } from "react-dom/client";

interface ReportsProps {
  title: string;
  page: PagedModel<ReportFragment>;
  columns: Column[];
  troubleshootUrlFactory?: (report: ReportFragment) => string;
  fetchUrl: string;
  demoReportUrl?: string;
  sort: Sort;
  patient: boolean;
  virtualConsultationUrl?: string;
  hasPendingReports: boolean;
  mobileApplicationView?: MobileApplicationView;
}

interface ReportsState {
  reportToArchive?: Report;
}

export default class Reports extends React.Component<ReportsProps, ReportsState> {
  constructor(props: ReportsProps) {
    super(props);
    this.state = {};
    this.listRef = React.createRef();
  }

  private readonly listRef: RefObject<ReportList>;

  render() {
    oberver_polyfill_activate();
    return (
      <>
        <HeaderBar
          title={this.props.title}
          action={
            this.props.mobileApplicationView && (
              <MobileApplicationLink mobileApplicationView={this.props.mobileApplicationView} />
            )
          }
        />
        {this.showDemoReportPanel() ? (
          <DemoReportPanel demoReportUrl={this.props.demoReportUrl} />
        ) : this.props.patient ? (
          <PatientReportsView
            ref={this.listRef}
            page={this.props.page}
            columns={this.props.columns}
            fetchUrl={this.props.fetchUrl}
            sort={this.props.sort}
            virtualConsultationUrl={this.props.virtualConsultationUrl}
            hasPendingReports={this.props.hasPendingReports}
          />
        ) : (
          <div className="paginated-list-container">
            <ReportList
              ref={this.listRef}
              fetchUrl={this.props.fetchUrl}
              sort={this.props.sort}
              page={this.props.page}
              columns={this.props.columns}
              troubleshootUrlFactory={this.props.troubleshootUrlFactory}
              classNames="with-shadow"
            />
          </div>
        )}
      </>
    );
  }

  private hasFilter = new URLSearchParams(document.location.search).getAll("q").length;

  private showDemoReportPanel() {
    return this.props.demoReportUrl && this.props.page.content.length === 0 && !this.hasFilter;
  }

  static loadComponent(elementId: string, props: ReportsProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<Reports {...props} />);
  }
}

function DemoReportPanel({ demoReportUrl }: { demoReportUrl?: string }) {
  return (
    <div className="container demo-report-panel">
      <div className="panel with-shadow h-flex">
        <div className="v-flex">
          <div className="title">{i18n["Page.Reports.DemoPanel.Title"]}</div>
          <p>{i18n["Page.Reports.DemoPanel.Content1"]}</p>
          <p>{i18n["Page.Reports.DemoPanel.Content2"]}</p>
          <div className="text-align-center">
            <a href={demoReportUrl} className="button-primary">
              {i18n["Page.Reports.DemoPanel.Action"]}
            </a>
          </div>
        </div>
        <div className="icon h-flex centered">
          <img src={ReportIcon} alt="report icon" />
        </div>
      </div>
    </div>
  );
}

function PatientReportsView({
  ref,
  page,
  columns,
  isArchive,
  fetchUrl,
  sort,
  virtualConsultationUrl,
  hasPendingReports,
}: {
  ref: React.RefObject<ReportList>;
  page: PagedModel<ReportFragment>;
  columns: Column[];
  isArchive?: boolean;
  fetchUrl: string;
  sort: Sort;
  virtualConsultationUrl?: string;
  hasPendingReports: boolean;
}) {
  return (
    <>
      {hasPendingReports && (
        <div className="container">
          <div className="message valid centered">{i18n["Page.Reports.Patient.PendingReport"]}</div>
        </div>
      )}
      {page.content.length > 0 ? (
        <div className="patient-report-panel">
          <div className="paginated-list-container">
            <ReportList
              ref={ref}
              fetchUrl={fetchUrl}
              sort={sort}
              page={page}
              columns={columns}
              disableFiltering={true}
              classNames="with-shadow"
            />
          </div>
        </div>
      ) : (
        <div className="container">
          <div className="no-report-panel">
            <div className="start-assessment-call-to-action-panel v-flex panel with-shadow centered">
              <svg
                width="140"
                height="120"
                viewBox="0 0 140 120"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g className="customized-svg">
                  <path d="M132.518 0H7.56242C3.72644 0 0.625 3.09572 0.625 6.92464V90.0204C0.625 93.8493 3.72644 96.945 7.56242 96.945H52.9413L49.8399 106.151H46.9017C43.0657 106.151 39.9643 109.246 39.9643 113.075V117.719C39.9643 119.022 41.0253 120 42.2495 120H97.7489C99.0548 120 100.034 118.941 100.034 117.719V113.075C100.034 109.246 96.9327 106.151 93.0967 106.151H90.1585L87.0571 96.945H132.436C136.272 96.945 139.373 93.8493 139.373 90.0204V6.92464C139.455 3.09572 136.354 0 132.518 0ZM54.7369 106.232L57.8383 97.0265H82.2417L85.3432 106.232H54.7369ZM93.1784 110.876C94.4842 110.876 95.4636 111.935 95.4636 113.157V115.438H44.6164V113.157C44.6164 111.853 45.6774 110.876 46.9017 110.876H93.1784ZM5.27715 6.92464C5.27715 5.62118 6.33817 4.64359 7.56242 4.64359H132.518C133.824 4.64359 134.803 5.70265 134.803 6.92464V90.0204C134.803 91.3238 133.742 92.3014 132.518 92.3014H7.56242C6.25655 92.3014 5.27715 91.2424 5.27715 90.0204V6.92464Z" />
                  <path d="M5.2771 38.126H134.803V41.629H5.2771V38.126Z" />
                  <path d="M25.436 20.611L25.1912 20.5295L25.3544 20.3666C26.2522 19.2261 26.7419 17.7597 26.7419 16.3747C26.6603 12.7902 23.7221 9.85742 20.1309 9.85742C16.5398 9.85742 13.52 12.7902 13.52 16.4562C13.52 17.9226 14.0097 19.3075 14.9075 20.4481L15.0707 20.611L14.8258 20.6925C11.0715 22.6476 8.70459 26.558 8.70459 30.7943C8.70459 31.7719 9.52076 32.5865 10.5002 32.5865C11.4796 32.5865 12.2957 31.7719 12.2957 30.7943C12.2957 26.4766 15.8052 22.9735 20.1309 22.9735C24.4566 22.9735 27.8845 26.4766 27.8845 30.7943C27.8845 31.7719 28.7007 32.5865 29.6801 32.5865C30.6595 32.5865 31.4757 31.7719 31.4757 30.7943C31.4757 26.3951 29.1904 22.5662 25.436 20.611ZM20.0493 19.2261C19.9677 19.2261 19.9677 19.2261 19.8861 19.2261C19.2331 19.2261 18.4986 18.9002 18.0089 18.4114C17.4376 17.8411 17.1111 17.1079 17.1111 16.2933C17.1111 14.6639 18.417 13.279 20.1309 13.279C21.8449 13.279 23.1508 14.5825 23.1508 16.2933C23.0691 17.9226 21.7633 19.2261 20.0493 19.2261Z" />
                  <path d="M65.429 22.4841H37.6793C36.6999 22.4841 35.9653 21.6694 35.9653 20.7733C35.9653 19.7957 36.7815 19.0625 37.6793 19.0625H65.429C66.4084 19.0625 67.1429 19.8772 67.1429 20.7733C67.1429 21.7509 66.4084 22.4841 65.429 22.4841Z" />
                  <path d="M84.6084 21.0999C84.6084 21.0999 84.9349 11.7312 84.9349 10.998C87.5466 10.998 90.0767 11.9756 92.0355 13.9308C91.5458 14.4196 84.6084 21.0999 84.6084 21.0999Z" />
                  <path d="M84.8532 9.53125C78.4871 9.53125 73.2637 14.7451 73.2637 21.0995C73.2637 27.4539 78.4871 32.6677 84.8532 32.6677C91.2193 32.6677 96.4428 27.4539 96.4428 21.0995C96.4428 14.7451 91.2193 9.53125 84.8532 9.53125ZM86.404 17.4335V12.7084C87.6282 12.9528 88.7708 13.3602 89.7502 14.0934L86.404 17.4335ZM83.3841 12.7084V21.0995C83.3841 21.7512 83.7106 22.24 84.2819 22.4844C84.4452 22.5659 84.69 22.5659 84.8532 22.5659C85.2613 22.5659 85.6694 22.4029 85.9143 22.1585L91.8723 16.2115C93.5862 18.737 93.9127 22.0771 92.5252 24.8469C91.1377 27.7797 88.1179 29.6535 84.8532 29.6535C80.3643 29.6535 76.6916 26.1504 76.3651 21.6697C76.1203 17.2706 79.1401 13.4416 83.3841 12.7084Z" />
                  <path d="M129.661 18.656H101.911C100.932 18.656 100.198 17.8413 100.198 16.9452C100.198 15.9676 101.014 15.2344 101.911 15.2344H129.661C130.641 15.2344 131.375 16.049 131.375 16.9452C131.375 17.8413 130.641 18.656 129.661 18.656Z" />
                  <path d="M129.661 27.1286H101.911C100.932 27.1286 100.198 26.314 100.198 25.4178C100.198 24.4402 101.014 23.707 101.911 23.707H129.661C130.641 23.707 131.375 24.5217 131.375 25.4178C131.375 26.314 130.641 27.1286 129.661 27.1286Z" />
                  <path d="M56.4506 50.5098L10.6636 50.8356L10.9084 81.9558L56.6954 81.6299L56.4506 50.5098Z" />
                  <path d="M56.5323 48.7168H10.7453C9.68433 48.7168 8.86816 49.5315 8.86816 50.5905V81.7107C8.86816 82.7698 9.68433 83.5844 10.7453 83.5844H56.5323C57.5933 83.5844 58.4095 82.7698 58.4095 81.7107V50.5905C58.4911 49.6129 57.5933 48.7168 56.5323 48.7168ZM54.6551 79.837H12.7041V52.5457H54.6551V79.837Z" />
                  <path d="M129.089 50.5098H66.3258C65.2648 50.5098 64.4486 51.3244 64.4486 52.3835C64.4486 53.4426 65.2648 54.2572 66.3258 54.2572H129.089C130.15 54.2572 130.966 53.4426 130.966 52.3835C130.966 51.3244 130.15 50.5098 129.089 50.5098Z" />
                  <path d="M66.3257 63.5443H70.9778C72.0388 63.5443 72.855 62.7297 72.855 61.6706C72.855 60.6115 72.0388 59.7969 70.9778 59.7969H66.3257C65.2647 59.7969 64.4485 60.6115 64.4485 61.6706C64.4485 62.7297 65.2647 63.5443 66.3257 63.5443Z" />
                  <path d="M66.3257 72.7504H70.9778C72.0388 72.7504 72.855 71.9357 72.855 70.8767C72.855 69.8176 72.0388 69.0029 70.9778 69.0029H66.3257C65.2647 69.0029 64.4485 69.8176 64.4485 70.8767C64.4485 71.9357 65.2647 72.7504 66.3257 72.7504Z" />
                  <path d="M66.3257 82.0375H70.9778C72.0388 82.0375 72.855 81.2228 72.855 80.1638C72.855 79.1047 72.0388 78.29 70.9778 78.29H66.3257C65.2647 78.29 64.4485 79.1047 64.4485 80.1638C64.4485 81.2228 65.2647 82.0375 66.3257 82.0375Z" />
                  <path d="M129.089 78.209H80.2009C79.1399 78.209 78.3237 79.0237 78.3237 80.0827C78.3237 81.1418 79.1399 81.9564 80.2009 81.9564H129.089C130.15 81.9564 130.967 81.1418 130.967 80.0827C130.967 79.0237 130.15 78.209 129.089 78.209Z" />
                  <path d="M129.089 68.9209H80.2009C79.1399 68.9209 78.3237 69.7356 78.3237 70.7946C78.3237 71.8537 79.1399 72.6684 80.2009 72.6684H129.089C130.15 72.6684 130.967 71.8537 130.967 70.7946C130.967 69.7356 130.15 68.9209 129.089 68.9209Z" />
                  <path d="M80.2009 63.5443H129.089C130.15 63.5443 130.967 62.7297 130.967 61.6706C130.967 60.6115 130.15 59.7969 129.089 59.7969H80.2009C79.1399 59.7969 78.3237 60.6115 78.3237 61.6706C78.3237 62.7297 79.1399 63.5443 80.2009 63.5443Z" />
                </g>
              </svg>
              <div className="instructions">
                {i18n["Page.Reports.Patient.NoReport.Instruction"]}
              </div>
              <a className="button-primary" href={virtualConsultationUrl}>
                {i18n["Page.Reports.Patient.StartSmileAssessment.Button"]}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
